import React, { useState } from 'react';
import { Phone, Shield, Sparkles, Clock, MapPin, ChevronRight, X } from 'lucide-react';
import CleaningImage from './media/cleaning.jpg';
import DetailImage from './media/detail.jpg';

const phoneNumber = '(509)998-2644';

const LandingPage = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const ContactForm = () => (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
        <button 
          onClick={() => setIsContactOpen(false)}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>
        
        <h3 className="text-2xl font-bold mb-2">Get Your Free Quote</h3>
        <p className="text-gray-600 mb-6">We'll get back to you within 2 hours</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input 
              type="text"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Your name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input 
              type="tel"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder='(509)555-1234'
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">What can we help with?</label>
            <select className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option>Carpet Deep Clean</option>
              <option>Tough Stain Removal</option>
              <option>Hardwood Floor Care</option>
              <option>Commercial Space</option>
              <option>Other (We'll Discuss)</option>
            </select>
          </div>
          
          <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors">
            Get Your Quote
          </button>
          
          <div className="text-center text-sm text-gray-500">
            Prefer to talk? Call {phoneNumber}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <header className="relative bg-gray-900 text-white">
        {/* Video Background */}
        <video 
            className="absolute top-0 left-0 w-full h-full object-cover z-0"
            src={'https://carpetbrosdotnetbucket.s3.us-west-2.amazonaws.com/carpetBrosHeroVideo.mp4'}
            autoPlay
            muted
            loop
            playsInline
        />
          <div className="absolute inset-0 bg-gray-900 opacity-60 z-0"></div>

        <div className="relative container mx-auto px-6 py-16 md:py-24 z-10">
          <div className="max-w-3xl">
            <div className="inline-block bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
              Now Serving Spokane & Surrounding Areas
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Executive-Level Floor Care at Fair Local Prices
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Professional carpet & floor cleaning with guaranteed results.
              <br />
              <span className="text-blue-400 font-semibold">Clean. No excuses.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => setIsContactOpen(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>Get Your Free Quote</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <a 
                href={"tel:"+phoneNumber}
                className="bg-white/10 hover:bg-white/20 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>{phoneNumber}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Value Props */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4">
              <Shield className="w-8 h-8 text-blue-500" />
              <div>
                <h3 className="font-semibold">100% Satisfaction Guarantee</h3>
                <p className="text-gray-600">Not happy? We'll make it right.</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <h3 className="font-semibold">Fast Response Time</h3>
                <p className="text-gray-600">Quote within 2 hours</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <MapPin className="w-8 h-8 text-blue-500" />
              <div>
                <h3 className="font-semibold">Proudly Local</h3>
                <p className="text-gray-600">Serving our Spokane community</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">The CarpetBros Difference</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <img src={CleaningImage} alt="Professional cleaning"   className="block mx-auto rounded-lg shadow-lg mb-6" width={500}/>
              <h3 className="text-xl font-bold mb-4">Executive-Level Service</h3>
              <p className="text-gray-600">
                We bring the same level of care and attention to detail that you'd expect in high-end corporate offices to your space. Using professional-grade equipment and proven techniques, we deliver results that exceed expectations.
              </p>
            </div>
            <div>
              <img src={DetailImage} alt="Attention to detail" className="block mx-auto rounded-lg shadow-lg mb-6" width={500}/>
              <h3 className="text-xl font-bold mb-4">Attention to Detail</h3>
              <p className="text-gray-600">
                Every job gets our full attention. We take pride in our work and treat your space with the respect it deserves. From tough stains to routine maintenance, we're committed to delivering exceptional results every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">Professional Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Carpet Deep Clean', desc: 'Thorough cleaning that removes deep-seated dirt and allergens' },
              { title: 'Stain Removal', desc: 'Advanced techniques for tough stains and high-traffic areas' },
              { title: 'Hardwood Care', desc: 'Gentle yet effective cleaning for hardwood floors' },
              { title: 'Commercial', desc: 'Flexible scheduling for businesses and offices' }
            ].map((service) => (
              <div key={service.title} className="bg-white p-6 rounded-lg shadow-sm">
                <Sparkles className="w-8 h-8 text-blue-500 mb-4" />
                <h3 className="font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready for Exceptional Results?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Get your free, no-obligation quote today and experience the difference professional care makes.
            </p>
            <button 
              onClick={() => setIsContactOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              Get Your Free Quote
            </button>
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg md:hidden p-4 z-40">
        <div className="flex gap-4">
          <button 
            onClick={() => setIsContactOpen(true)}
            className="flex-1 bg-blue-500 text-white py-3 rounded-lg font-semibold"
          >
            Get Quote
          </button>
          <a 
            href={"tel:"+phoneNumber}
            className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold text-center"
          >
            Call Now
          </a>
        </div>
      </div>

      {isContactOpen && <ContactForm />}
    </div>
  );
};

export default LandingPage;